import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { LoyaltySummary } from '../types/loyalty';

interface LoyaltyState {
  summary: LoyaltySummary | null;
  isLoading: boolean;
  error: string | null;
  fetchAndUpdateLoyaltySummary: () => Promise<void>;
}

export const useLoyaltyStore = create<LoyaltyState>((set) => ({
  summary: null,
  isLoading: false,
  error: null,

  fetchAndUpdateLoyaltySummary: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error: rpcError } = await supabase
        .rpc('get_my_loyalty_summary');


      if (rpcError) {
        throw new Error('Failed to fetch loyalty summary');
      }

      if (!data) {
        set({ summary: null, isLoading: false });
        return;
      }

      // Handle array response - take the first element
      const summaryData = Array.isArray(data) ? data[0] : data;
      
      // Ensure we treat the data as valid even if points are 0
      // Create a default summary if needed with 0 points
      if (summaryData) {
        // Make sure we have valid data with at least the required fields
        const validSummary = {
          total_points_earned: summaryData.total_points_earned ?? 0,
          current_points: summaryData.current_points ?? 0,
          tier_name: summaryData.tier_name || 'Insider',
          tier_description: summaryData.tier_description || 'Welcome to Zap!',
          tier_color_hex: summaryData.tier_color_hex || '#6B7280',
          next_tier_name: summaryData.next_tier_name || null,
          next_tier_minimum_points: summaryData.next_tier_minimum_points || null,
          points_to_next_tier: summaryData.points_to_next_tier || null
        };
        set({ summary: validSummary, isLoading: false });
      } else {
        // This shouldn't happen with our updated database function, but just in case
        set({ 
          summary: {
            total_points_earned: 0,
            current_points: 0,
            tier_name: 'Insider',
            tier_description: 'Welcome to Zap!',
            tier_color_hex: '#6B7280',
            next_tier_name: null,
            next_tier_minimum_points: null,
            points_to_next_tier: null
          }, 
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof Error ? error.message : 'An unknown error occurred',
        summary: null 
      });
    }
  },
}));
